import { useFormikContext } from "formik";
import { CheckCircleIcon } from "vega-icons";

import { type DealPermissionUser } from "@vega-api/generated/types/dealPermissionUser";
import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import {
  Accordion,
  AccordionVariant,
} from "@vega-core/components/Accordion/Accordion";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { m } from "@vega-core/paraglide/messages";

import { type EmailEditModalFormValues } from "../EmailEditModal";

type ContactsStepProps = {
  isSendEmailPending: boolean;
  closeModal: () => void;
  prospectsWithUserList: (DealShortlistItemResponse & {
    users: DealPermissionUser[];
  })[];
  onBack: () => void;
  onSend: () => void;
};

/**
 * Note: styling must match with renderUsers to make the columns look consistent
 */
const renderTableHeader = () => (
  <div className="flex flex-col gap-4" data-testid="contacts-step-header-row">
    <div className="pl-4 sm:pl-6 flex items-center flex-wrap">
      <div className="grow w-0 min-w-40" />
      <div className="flex grow sm:grow-0">
        <span className="flex justify-center items-center grow w-28 h-12 body-base-medium text-text/primary">
          {m.coInvestments_outreach_unlockAccess_emailColumnHeader()}
        </span>
        <span className="flex justify-center items-center grow w-28 h-12 body-base-medium text-text/primary">
          {m.coInvestments_outreach_unlockAccess_unlockAccessColumnHeader()}
        </span>
      </div>
    </div>
  </div>
);

const renderUsers = ({
  users,
  recipientsIdsSet,
  isMultipleProspects,
}: {
  users: DealPermissionUser[];
  recipientsIdsSet: Set<string>;
  isMultipleProspects: boolean;
}) => (
  <div className="flex flex-col gap-4" data-testid="contacts-step-users">
    {users.map((user) => (
      <div
        className="pl-4 sm:pl-6 flex items-center flex-wrap"
        data-testid={`contacts-step-user-row-${user.id}`}
        key={user.id}
      >
        <div className="flex flex-col text-start grow w-0 min-w-40 py-1">
          <span className="body-base-medium text-text/primary truncate">
            {user.fullName}
          </span>
          <span className="body-small-regular text-text/secondary truncate">
            {user.email}
          </span>
        </div>
        {isMultipleProspects ? null : (
          <div className="flex grow sm:grow-0">
            <span className="flex justify-center items-center grow w-28 h-12">
              {recipientsIdsSet.has(user.id) ? (
                <CheckCircleIcon
                  className="size-4 text-text/positive"
                  data-testid={`${user.id}-email-check-icon`}
                />
              ) : null}
            </span>
            <span className="flex justify-center items-center grow w-28 h-12">
              <CheckCircleIcon
                className="size-4 text-text/positive"
                data-testid={`${user.id}-unlock-access-check-icon`}
              />
            </span>
          </div>
        )}
      </div>
    ))}
  </div>
);

const renderModalDescription = ({
  isMultipleProspects,
}: {
  isMultipleProspects: boolean;
}) =>
  isMultipleProspects
    ? m.coInvestments_editEmail_contacts_description_multipleProspects()
    : m.coInvestments_editEmail_contacts_description();

export const ContactsStep = ({
  isSendEmailPending,
  closeModal,
  prospectsWithUserList,
  onBack,
  onSend,
}: ContactsStepProps) => {
  const isMultipleProspects = prospectsWithUserList.length > 1;
  const firstProspect = prospectsWithUserList[0];

  const {
    values: { to, cc },
  } = useFormikContext<EmailEditModalFormValues>();

  const recipientsIdsSet = new Set([
    ...to.map((recipient) => recipient.id),
    ...cc.map((recipient) => recipient.id),
  ]);

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainAction: onSend,
        mainActionText: m.btn_send(),
        disabled: isSendEmailPending,
        secondaryAction: onBack,
        secondaryActionText: m.btn_back(),
      }}
      dataTestId="email-edit-modal-contacts-step"
      description={
        <span data-testid="contacts-step-description">
          {renderModalDescription({
            isMultipleProspects,
          })}
        </span>
      }
      fluidHeight
      open
      setOpen={closeModal}
      title={m.coInvestments_outreach_unlockAccess_modal_title()}
    >
      {isMultipleProspects ? null : renderTableHeader()}
      <div className="flex flex-col gap-8">
        {isMultipleProspects ? (
          <Accordion
            defaultValue={prospectsWithUserList.map((prospect) => prospect.id)}
            items={prospectsWithUserList.map((prospect) => ({
              value: prospect.id,
              title: `${prospect.limitedPartner.name} (${prospect.users.length})`,
              content: renderUsers({
                users: prospect.users,
                recipientsIdsSet,
                isMultipleProspects,
              }),
            }))}
            type="multiple"
            variant={AccordionVariant.FAQ}
          />
        ) : (
          renderUsers({
            users: firstProspect.users,
            recipientsIdsSet,
            isMultipleProspects,
          })
        )}
      </div>
    </ActionaryModal>
  );
};
